import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOpenLobbyStore } from "../lib/games/openlobby/store";
import { useGameSocket } from "../lib/useGameSocket";
import { trackEvent } from "../lib/analytics";
import { Wikipage } from "../components/Wikipage";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";
import { ThemeToggle } from "../components/ThemeToggle";
import { Timer } from "../components/Timer";
import { playFoundSound } from "../lib/sounds";
import { HomeLink } from "../components/HomeLink";
import { GameLoadingScreen } from "../components/GameLoadingScreen";
import { MobileNav } from "../components/navbar/NavbarMobile";
import { NavButtons } from "../components/navbar/NavbarButtons";
import { TargetBadge } from "../components/TargetBadge";

export const Route = createFileRoute("/openlobby/$lobbyId/game")({
  component: OpenLobbyGamePage,
});

function OpenLobbyGamePage() {
  const { lobbyId } = Route.useParams() as { lobbyId: string };
  const navigate = useNavigate();
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);
  const { t } = useTranslation();
  const { send } = useGameSocket(lobbyId, useOpenLobbyStore);

  const gameState = useOpenLobbyStore((state) => state.gameState);
  const currentPlayerId = useAppStore((state) => state.id);

  const navbarButtonsIngame = [NavButtons.wiki, NavButtons.targets] as const;
  const [mobileTabIngame, setMobileTabIngame] = useState<
    (typeof navbarButtonsIngame)[number]
  >(NavButtons.wiki);

  const hasFinished = gameState
    ? (gameState.players[currentPlayerId]?.found_targets?.length ?? 0) > 0
    : false;
  const prevHasFinishedRef = useRef(hasFinished);

  useEffect(() => {
    const justFinished = hasFinished && !prevHasFinishedRef.current;
    prevHasFinishedRef.current = hasFinished;

    if (gameState?.phase === "Score") {
      navigate({ to: `/openlobby/${lobbyId}/score` });
      return;
    }

    if (justFinished) {
      trackEvent("openlobby-game-found-target", { lobbyId });
      playFoundSound();
      navigate({ to: `/openlobby/${lobbyId}/score` });
    }
  }, [gameState?.phase, hasFinished, lobbyId, navigate]);

  if (!gameState) {
    return <GameLoadingScreen />;
  }

  if (gameState.phase !== "Game") {
    return null;
  }

  return (
    <div
      className={`h-dvh ${isDark ? "bg-black" : "bg-white"} flex flex-col font-sans overflow-hidden`}
    >
      <div
        className={`shrink-0 z-50 border-b ${isDark ? "border-white/[0.08] bg-black/60" : "border-black/[0.06] bg-white/70"} backdrop-blur-xl`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center">
              <HomeLink />
            </div>

            <div className="hidden md:flex items-center justify-center gap-1.5">
              <TargetBadge
                article={gameState.target_article}
                isFound={false}
                theme={theme}
                isDark={isDark}
                className="text-xs py-1 px-2 w-auto"
              />
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div
                className={`flex items-center gap-1.5 font-mono ${isDark ? "text-zinc-400" : "text-gray-600"} text-sm sm:text-xs`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <Timer
                  startTime={gameState.start_time}
                  duration={gameState.game_duration}
                  isDark={isDark}
                />
              </div>

              <div
                className={`hidden sm:block w-px h-4 ${isDark ? "bg-zinc-800" : "bg-gray-200"}`}
              />

              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
            </div>
          </div>
        </div>
      </div>

      <MobileNav
        currentTab={mobileTabIngame}
        onTabChange={setMobileTabIngame}
        isDark={isDark}
        theme={theme}
        buttons={navbarButtonsIngame}
        className="flex-1"
      >
        <div className="relative z-10 h-full overflow-hidden flex flex-col">
          <section
            className={`h-full overflow-hidden flex justify-center ${mobileTabIngame === NavButtons.wiki ? "block" : "hidden md:block"}`}
          >
            <Wikipage isDark={isDark} send={send} />
          </section>

          <section
            className={`px-4 py-4 space-y-2 md:hidden ${mobileTabIngame === NavButtons.targets ? "block" : "hidden"}`}
          >
            <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>
              {t("openLobby.target")}
            </h3>
            <div className="flex flex-col justify-start gap-2">
              <TargetBadge
                article={gameState.target_article}
                isFound={false}
                theme={theme}
                isDark={isDark}
                className="text-xs py-1.5 px-2.5"
              />
            </div>
          </section>
        </div>
      </MobileNav>
    </div>
  );
}
